'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import CalendarBarItem, {
    CalendarBarItemProps,
    CalendarBarItemStyle
} from '@/app/[locale]/[domain]/(default-layout)/program/_components/CalendarBarItem';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { calendarBarLabelInstant, isDateToday, formatDate_yyyyMMdd } from '@/utils/date-helper';

export type MovieTicketingModalCalendarBarItemProps = {
    id?: string;
    classNameProgramListCalendarBarItem?: string;
    date?: Date;
    selectedDate?: string;
    setSelectedDate?: (date: string) => void;
    showingsNumber: { value: number | undefined; isLoading: boolean | undefined };
    viewBelow?: (date?: Date) => React.JSX.Element;
    widthMin?: boolean;
    widerItemSizeOnMobile?: boolean;
} & Omit<CalendarBarItemProps, 'dateItemWidth' | 'className'>;

function ProgramListCalendarBarItem(props: MovieTicketingModalCalendarBarItemProps): React.JSX.Element {
    const {
        id,
        classNameProgramListCalendarBarItem,
        title,
        date,
        selectedDate,
        setSelectedDate,
        showingsNumber,
        viewBelow,
        widerItemSizeOnMobile
    } = props;

    const t = useTranslations();
    const { timeZone } = useDomainConfiguration();
    const format = useFormat({ timeZone });
    const labelDate = date ? calendarBarLabelInstant(date, timeZone) : undefined;

    const weekday = labelDate ? format.dateTime.formatWeekday(labelDate).replace('.,', '') : undefined;
    const dayLabel = date && isDateToday(date, timeZone) ? t('program.today') : weekday;
    const dayAndMonth = labelDate ? format.dateTime.formatDayMonth(labelDate) : undefined;
    const dateForUrl = date ? formatDate_yyyyMMdd(date) : undefined;
    const isItemActive = props.isAlwaysHighlighted ?? props.isItemActive ?? selectedDate === dateForUrl;

    const accessibilityDate = labelDate
        ? format.dateTime.formatDate(labelDate, {
              weekday: 'long',
              day: 'numeric',
              month: 'long'
          })
        : title;
    const accessibilityLabel = t('program.calendar-bar-item-accessibility-label', {
        date: accessibilityDate ?? '',
        showings: showingsNumber?.value ?? 0
    });

    const isDisabled = props.isDisabled;

    const showingsNumberTextColor = (() => {
        if (isItemActive) {
            return 'text-darkBlue';
        } else {
            return isDisabled ? 'text-gray-middle' : 'text-gray-dark';
        }
    })();

    return (
        <div
            className={cn(
                'flex flex-col space-y-12 shrink-0',
                !props.widthMin ? (widerItemSizeOnMobile ? 'w-[200px]' : 'min-w-[90px]') : undefined,
                !props.widthMin ? (widerItemSizeOnMobile ? 'lg:w-[200px]' : 'lg:min-w-[134px]') : undefined
            )}>
            <CalendarBarItem
                id={id}
                className={cn(classNameProgramListCalendarBarItem)}
                isItemActive={isItemActive}
                isItemSelected={Boolean(dateForUrl) && selectedDate === dateForUrl}
                onItemClick={() => {
                    props.onItemClick?.();
                    if (dateForUrl) setSelectedDate?.(dateForUrl);
                }}
                dateItemWidth="w-full"
                calendarItemStyle={props.calendarItemStyle || CalendarBarItemStyle.On_white}
                isDisabled={isDisabled}
                isScrollTarget={(showingsNumber?.value ?? 0) > 0}
                accessibilityLabel={accessibilityLabel}
                customView={
                    <div className={cn('w-full h-full', 'flex flex-col flex-none justify-center items-center')}>
                        {showingsNumber &&
                            (showingsNumber.isLoading ? (
                                <div
                                    className={cn(
                                        'absolute top-4 right-4 w-10 h-10 bg-gray-middle/50 rounded-2 animate-pulse'
                                    )}
                                />
                            ) : (
                                <div
                                    aria-hidden="true"
                                    className={cn('absolute top-2 right-4 text-body-10', showingsNumberTextColor)}
                                    style={
                                        typeof props.calendarItemStyle === 'object'
                                            ? {
                                                  color: isItemActive
                                                      ? props.calendarItemStyle.textColorItemActive
                                                      : props.calendarItemStyle.textColor
                                              }
                                            : undefined
                                    }>
                                    {showingsNumber?.value}
                                </div>
                            ))}
                        {dayLabel && <span>{dayLabel.toUpperCase()}</span>}
                        {dayAndMonth && <span>{dayAndMonth}</span>}
                        {title && <span>{title}</span>}
                    </div>
                }
            />
            {viewBelow && viewBelow(date)}
        </div>
    );
}

export default ProgramListCalendarBarItem;
